import { useCallback, useEffect, useMemo, useState } from 'react';

import { SeedFilter, SeedFilterKeys, Sort } from '@/hooks/useSeeds';
import { cleanupFiltersForTab } from '@/sections/seeds/config/filters';
import { getDefaultSort } from '@/sections/seeds/config/seedTypeConfigs';
import { SeedType } from '@/sections/seeds/types';
import { seedUrlParams } from '@/sections/seeds/utils/seedUrlParams';
import { seedFilterStorage } from '@/sections/seeds/utils/storage/seedFilterStorage';
import { useAuth } from '@/state/auth';
import { SeedStatus } from '@/types';
import { omit } from '@/utils/lodash.util';

/**
 * Seed filter state management with hybrid persistence
 *
 * **Config-driven implementation** - Uses getDefaultSort() for all types
 *
 * Manages filter state with dual persistence mechanism:
 * - Primary: localStorage for user preferences across sessions
 * - Secondary: URL parameters for shareability and deep linking
 *
 * Priority order for initialization:
 * 1. URL parameters (for shared links)
 * 2. localStorage (user preferences)
 * 3. Default filters from config
 *
 * @param seedType - The active seed type (domain/ip/webapplication/preseed)
 * @returns Filter state and management functions
 */
export const useSeedFilters = (seedType: SeedType) => {
  const { isPraetorianUser } = useAuth();

  // 1. Initialize state (priority: URL → localStorage → config defaults)
  const [filters, setFiltersState] = useState<SeedFilter>(() => {
    // Get default configuration for this seed type
    const defaultSort = getDefaultSort(seedType) as Sort;
    const DEFAULT_FILTERS: SeedFilter = {
      type: seedType,
      sort: defaultSort,
    };

    // Priority 1: URL params (for shared links)
    const urlFilters = seedUrlParams.read();

    // Priority 2: localStorage (user preferences)
    // Strip search so it doesn't survive page reloads (ephemeral)
    const storedRaw = seedFilterStorage.get(seedType);
    const storedFilters = storedRaw
      ? { ...storedRaw, search: undefined }
      : storedRaw;

    // Merge: URL params override localStorage
    const mergedFilters = {
      ...(storedFilters || DEFAULT_FILTERS),
      ...urlFilters,
      type: seedType, // Always use the current seed type
    };

    // Clean up invalid filters for the current tab (config-driven)
    const cleanedFilters = cleanupFiltersForTab(mergedFilters, seedType);

    return cleanedFilters as SeedFilter;
  });

  // 2. Sanitize stale Delete status filter for non-Praetorian users.
  // Computed as a derived value (not mutated state) so that it re-evaluates
  // whenever isPraetorianUser resolves — avoiding the auth-load race that a
  // useEffect([]) would introduce (isPraetorianUser is false during auth load,
  // which would incorrectly strip Delete from Praetorian users' filters).
  const sanitizedFilters: SeedFilter = useMemo(() => {
    if (isPraetorianUser) return filters;
    const status = filters.advancedFilters?.status;
    if (!status || !status.includes(SeedStatus.Delete)) return filters;
    return {
      ...filters,
      advancedFilters: {
        ...filters.advancedFilters,
        status: status.filter(s => s !== SeedStatus.Delete),
      },
    };
  }, [isPraetorianUser, filters]);

  // 3. Persist to localStorage on change
  useEffect(() => {
    // Tab-switch guard: for one commit after the `seedType` prop changes,
    // `filters` still holds the OUTGOING tab's state (the type-change reset
    // effect below runs later in the same commit). Persisting in that window
    // writes the outgoing tab's filters under the incoming tab's storage key,
    // destroying the incoming tab's saved filters and leaking filters across
    // tabs. `filters.type` is always forced to the active type by the
    // initializer and by cleanupFiltersForTab, so this never suppresses a
    // legitimate write.
    if (filters.type !== seedType) return;
    seedFilterStorage.set(seedType, filters);
  }, [seedType, filters]);

  // 3. Sync basic filters to URL (debounced via timeout)
  useEffect(() => {
    const timer = setTimeout(() => {
      seedUrlParams.write({
        type: filters.type,
        search: filters.search,
      });
    }, 500); // Debounce URL updates to avoid excessive history changes

    return () => clearTimeout(timer);
  }, [filters.type, filters.search]);

  // 4. Update filters when seed type changes (config-driven cleanup)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFiltersState(prev => {
      // If type hasn't changed, don't update
      if (prev.type === seedType) return prev;

      // Get default configuration for this seed type
      const defaultSort = getDefaultSort(seedType) as Sort;

      // Load stored filters for the new type
      // Strip search so it doesn't survive page reloads (ephemeral)
      const storedRaw = seedFilterStorage.get(seedType);
      const storedFilters = storedRaw
        ? { ...storedRaw, search: undefined }
        : storedRaw;
      const baseFilters = storedFilters || {
        type: seedType,
        sort: defaultSort,
      };

      // Apply universal tab-specific filter cleanup (works for ALL types)
      const cleanedFilters = cleanupFiltersForTab(baseFilters, seedType);

      return cleanedFilters as SeedFilter;
    });
  }, [seedType]);

  // 5. Filter update functions
  const setFilters = useCallback(
    (newFilters: SeedFilter | ((prev: SeedFilter) => SeedFilter)) => {
      setFiltersState(newFilters);
    },
    []
  );

  const setSearch = useCallback((value: string) => {
    setFiltersState(prev => ({
      ...prev,
      search: value,
    }));
  }, []);

  const setSort = useCallback((value: Sort) => {
    setFiltersState(prev => ({ ...prev, sort: value }));
  }, []);

  const setAdvancedFilters = useCallback(
    (key: SeedFilterKeys, value: string[]) => {
      setFiltersState(prev => {
        if (value.length > 0) {
          return {
            ...prev,
            advancedFilters: { ...prev?.advancedFilters, [key]: value },
          };
        } else {
          const updatedFilters = {
            ...prev,
            advancedFilters: omit(prev?.advancedFilters, key),
          };
          return updatedFilters;
        }
      });
    },
    []
  );

  // 6. Reset helper (config-driven)
  const resetFilters = useCallback(() => {
    // Get default sort from configuration for this type
    const defaultSort = getDefaultSort(seedType) as Sort;
    const defaultForType: SeedFilter = {
      type: seedType,
      sort: defaultSort,
    };

    setFiltersState(defaultForType);
    seedFilterStorage.clear(seedType);
    seedUrlParams.clear();
  }, [seedType]);

  return {
    filters: sanitizedFilters,
    setFilters,
    setSearch,
    setSort,
    setAdvancedFilters,
    resetFilters,
  };
};
